import { EyeOutlined } from "@ant-design/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Alert, Button, Checkbox, Dropdown, Input, message, Modal, Skeleton, Table, Tooltip } from "antd"
import React from "react"
import { Link, useNavigate } from "react-router-dom"
import { ContainerFull } from "../components"

// You should implement this endpoint in your endpoints/index.ts
import { getRuns, updateRun, regenerateAllRunSettings, queueAnalysis, getNextflowRunStatuses, cleanAllRunFolders } from "../endpoints"
import type { RunStatusType } from "../types"

const RUN_STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "running", label: "Running" },
    { value: "test", label: "Test" },
    { value: "completion-detected", label: "Completion Detected" },
    { value: "completed", label: "Completed" },
    { value: "canceled", label: "Canceled" },
]

const getStatusStyle = (status: string) => {
    switch (status) {
        case "new":
            return { color: "black", backgroundColor: "LightGreen" }
        case "running":
            return { color: "black", backgroundColor: "YellowGreen" }
        case "completion-detected":
            return { color: "black", backgroundColor: "MediumTurquoise" }
        case "completed":
            return { color: "white", backgroundColor: "green" }
        case "canceled":
            return { color: "black", backgroundColor: "OldLace" }
        default:
            return undefined
    }
}

export const AllRuns = (): JSX.Element => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    // Pagination & filter state — all sent to server
    const [page, setPage] = React.useState(1)
    const [pageSize, setPageSize] = React.useState(20)
    const [hideDeadFlowCells, setHideDeadFlowCells] = React.useState(false)
    const [showOnlyMostRecentFlowCellUse, setShowOnlyMostRecentFlowCellUse] = React.useState(false)
    const [showOnlyDeadFlowCells, setShowOnlyDeadFlowCells] = React.useState(false)
    const [flowCellFilter, setFlowCellFilter] = React.useState("")
    const [flowCellFilterInput, setFlowCellFilterInput] = React.useState("")

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])
    const [nextflowStatuses, setNextflowStatuses] = React.useState<Record<number, string>>({})
    const [cleanFailedRuns, setCleanFailedRuns] = React.useState<{ runId: number; message: string }[]>([])
    
    const { isPending, isError, data, error } = useQuery({
        queryKey: ["allruns", page, pageSize, hideDeadFlowCells, showOnlyDeadFlowCells, showOnlyMostRecentFlowCellUse, flowCellFilter],
        queryFn: () => getRuns({
            type: 'all',
            page,
            pageSize,
            hideDeadFlowCells,
            showOnlyDeadFlowCells,
            showOnlyMostRecentFlowCellUse,
            flowCellFilter: flowCellFilter || undefined,
        }),
        refetchOnMount: "always",
    })

    const updateRunMutation = useMutation({
        mutationFn: ({ runId, status }: { runId: number; status: RunStatusType }) =>
            updateRun({ runId, status }),
        onSuccess: () => {
            message.success("Run status updated successfully")
            queryClient.invalidateQueries({ queryKey: ["allruns"] })
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to update run status")
        },
    })

    const regenerateAllMutation = useMutation({
        mutationFn: regenerateAllRunSettings,
        onSuccess: (response: any) => {
            if (response.code === 1000) {
                message.success(response.message || "All run settings regenerated successfully")
            } else {
                message.error(response.message || "Failed to regenerate all run settings")
            }
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to regenerate all run settings")
        },
    })

    const cleanAllRunFoldersMutation = useMutation({
        mutationFn: cleanAllRunFolders,
        onSuccess: (response: any) => {
            console.log("cleanAllRunFolders response:", response)
            if (response.code === 1000) {
                const failed: { runId: number; message: string }[] = Array.isArray(response.failed) ? response.failed : []
                setCleanFailedRuns(failed)
                if (failed.length > 0) {
                    message.warning(response.message || "Finished cleaning run folders with some failures")
                } else {
                    message.success(response.message || "Finished cleaning all run folders")
                }
            } else {
                setCleanFailedRuns([])
                message.error(response.message || "Failed to clean run folders")
            }
        },
        onError: (error: any) => {
            setCleanFailedRuns([])
            message.error(error?.message || "Failed to clean run folders")
        },
    })

    const handleCleanAllRunFolders = () => {
        Modal.confirm({
            title: 'Clean All Run Folders',
            content: (
                <p>
                    This will go through every run, one by one, and delete the <code>work</code> and
                    <code> peekpath/work </code> folders and the contents of each subfolder under
                    <code> no_sample_id </code> in its run folder. This cannot be undone. Continue?
                </p>
            ),
            okText: 'Clean All',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: () => {
                cleanAllRunFoldersMutation.mutate()
            },
        })
    }

    const queueAnalysisMutation = useMutation({
        mutationFn: queueAnalysis,
        onSuccess: (response: any) => {
            if (response.code === 1000) {
                message.success(response.message || "Analysis queued successfully")
                setSelectedRowKeys([]) // Clear selection after successful queue
            } else {
                message.error(response.message || "Failed to queue analysis")
            }
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to queue analysis")
        },
    })

    const handleStatusChange = (runId: number, newStatus: RunStatusType) => {
        updateRunMutation.mutate({ runId, status: newStatus })
    }

    const handleRegenerateAll = () => {
        if (window.confirm("Are you sure you want to regenerate settings for ALL runs? This may take a while.")) {
            regenerateAllMutation.mutate()
        }
    }

    // Fetch Nextflow status for all runs
    React.useEffect(() => {
        const fetchAllNextflowStatuses = async () => {
            if (!data?.runs || data.runs.length === 0) return
            
            try {
                const runIds = data.runs.map((run: any) => run.id)
                console.log(`Fetching Nextflow statuses for ${runIds.length} runs...`)
                const response = await getNextflowRunStatuses({ runIds })
                
                console.log('Nextflow statuses response:', response)
                
                if (response.code === 1000 && response.statuses && Array.isArray(response.statuses)) {
                    const statuses: Record<number, string> = {}
                    response.statuses.forEach(({ runId, status }) => {
                        // Ensure runId is a number (GPU server might return it as string or number)
                        const numericRunId = typeof runId === 'string' ? parseInt(runId, 10) : runId
                        statuses[numericRunId] = status || "not running"
                    })
                    console.log(`Successfully fetched ${response.statuses.length} nextflow statuses`)
                    setNextflowStatuses(statuses)
                } else if (response.code === 503) {
                    // GPU server is unavailable
                    console.warn('GPU server unavailable:', response.message)
                    // Show user-friendly message
                    const statuses: Record<number, string> = {}
                    data.runs.forEach((run: any) => {
                        statuses[run.id] = "not running"
                    })
                    setNextflowStatuses(statuses)
                    // Optionally show a notification to the user
                    if (response.message) {
                        console.info(response.message)
                    }
                } else {
                    console.warn('Invalid response from getNextflowRunStatuses:', response)
                    // Fallback: mark all as not running
                    const statuses: Record<number, string> = {}
                    data.runs.forEach((run: any) => {
                        statuses[run.id] = "not running"
                    })
                    setNextflowStatuses(statuses)
                }
            } catch (err) {
                console.error("Failed to fetch Nextflow statuses:", err)
                // Fallback: mark all as not running
                const statuses: Record<number, string> = {}
                data.runs.forEach((run: any) => {
                    statuses[run.id] = "not running"
                })
                setNextflowStatuses(statuses)
            }
        }
        
        fetchAllNextflowStatuses()
        
        // Poll every 30 seconds
        const interval = setInterval(fetchAllNextflowStatuses, 30000)
        return () => clearInterval(interval)
    }, [data?.runs])

    const handleQueueAnalysis = () => {
        if (selectedRowKeys.length === 0) {
            message.warning("Please select at least one run to queue for analysis")
            return
        }
        
        if (!data?.runs) {
            message.error("No run data available")
            return
        }
        
        // Normalize selected key types (antd keys may be string or number)
        const selectedRunIdSet = new Set(
            selectedRowKeys
                .map((key) => Number(key))
                .filter((id) => Number.isFinite(id))
        )

        // Get the full run data for selected runs
        const selectedRuns = data.runs.filter((run: any) => selectedRunIdSet.has(Number(run.id)))
        if (selectedRuns.length === 0) {
            message.error("No valid selected runs found")
            return
        }

        // Check if any selected run already has Nextflow running
        const runningSelectedRuns = selectedRuns
            .filter((run: any) => nextflowStatuses[Number(run.id)] === "running")
            .map((run: any) => run.id)

        if (runningSelectedRuns.length > 0) {
            message.error(`Cannot queue analysis: Selected run(s) ${runningSelectedRuns.join(", ")} already have Nextflow analysis running.`)
            return
        }

        // Sort by duration in ascending order (invalid/null durations last), tie-break by run ID
        const toSortableDuration = (value: unknown): number => {
            const n = typeof value === "number" ? value : typeof value === "string" ? parseFloat(value) : NaN
            return Number.isFinite(n) && n >= 0 ? n : Number.POSITIVE_INFINITY
        }

        const sortedRuns = [...selectedRuns].sort((a: any, b: any) => {
            const diff = toSortableDuration(a.duration) - toSortableDuration(b.duration)
            if (diff !== 0) return diff
            return Number(a.id) - Number(b.id)
        })
        
        const sortedRunIds = sortedRuns.map((run: any) => run.id)
        
        Modal.confirm({
            title: 'Queue Analysis',
            content: (
                <p>Queue {selectedRowKeys.length} run(s) for analysis?</p>
            ),
            onOk: () => {
                console.log("Queueing analysis for runs (sorted by duration):", sortedRunIds)
                queueAnalysisMutation.mutate({ runIds: sortedRunIds })
            },
            okText: 'Queue',
            cancelText: 'Cancel'
        })
    }

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys: React.Key[]) => {
            setSelectedRowKeys(selectedKeys)
        },
    }

    if (isPending) return <Skeleton />
    if (isError || data.code !== 1000) return <div>Error: {error?.message || data?.message || "Unknown error"}</div>

    // Get the most recent run ID for each flow cell (from current page)
    const getMostRecentRunForEachFlowCell = () => {
        const flowCellToMostRecentRunId = new Map<string, number>()
        
        if (!data?.runs) return flowCellToMostRecentRunId
        
        data.runs.forEach((run: any) => {
            if (run.flowCellId) {
                const existing = flowCellToMostRecentRunId.get(run.flowCellId)
                if (!existing || run.id > existing) {
                    flowCellToMostRecentRunId.set(run.flowCellId, run.id)
                }
            }
        })
        
        return flowCellToMostRecentRunId
    }

    const mostRecentRunForFlowCell = getMostRecentRunForEachFlowCell()

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <h2>All Runs</h2>
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                 
                <Button 
                    type="primary" 
                    onClick={handleQueueAnalysis}
                    disabled={selectedRowKeys.length === 0}
                    loading={queueAnalysisMutation.isPending}
                >
                    Queue Analysis ({selectedRowKeys.length})
                </Button>
                <Button 
                    type="default" 
                    onClick={() => queryClient.invalidateQueries({ queryKey: ["allruns", page, pageSize, hideDeadFlowCells, showOnlyDeadFlowCells, showOnlyMostRecentFlowCellUse, flowCellFilter] })}
                    loading={isPending}
                >
                    Refresh
                </Button>
                <Button
                    type="default"
                    onClick={handleRegenerateAll}
                    loading={regenerateAllMutation.isPending}
                >
                    Regenerate Settings for All Runs
                </Button>
                <Button                    
                    onClick={handleCleanAllRunFolders}
                    loading={cleanAllRunFoldersMutation.isPending}
                >
                    Clean Run Folders
                </Button>
               
                <Checkbox
                    checked={hideDeadFlowCells}
                    onChange={(e) => { setHideDeadFlowCells(e.target.checked); setPage(1) }}
                >
                    Hide runs with dead flow cells
                </Checkbox>
                <Checkbox
                    checked={showOnlyDeadFlowCells}
                    onChange={(e) => { setShowOnlyDeadFlowCells(e.target.checked); setPage(1) }}
                >
                    Show only runs with dead flow cells
                </Checkbox>
                <Checkbox
                    checked={showOnlyMostRecentFlowCellUse}
                    onChange={(e) => { setShowOnlyMostRecentFlowCellUse(e.target.checked); setPage(1) }}
                >
                    Show only most recent flow cell use
                </Checkbox>
            </div>
            {cleanFailedRuns.length > 0 && (
                <Alert
                    type="error"
                    showIcon
                    closable
                    onClose={() => setCleanFailedRuns([])}
                    style={{ marginBottom: 12 }}
                    message={`${cleanFailedRuns.length} run folder(s) failed to clean`}
                    description={
                        <div>
                            <p style={{ marginBottom: 8 }}>
                                Failed run numbers: <strong>{cleanFailedRuns.map((f) => f.runId).join(", ")}</strong>
                            </p>
                            <ul style={{ maxHeight: 220, overflowY: "auto", paddingLeft: 20, marginBottom: 0 }}>
                                {cleanFailedRuns.map((f) => (
                                    <li key={f.runId}>
                                        <Link to={`/admin/run/${f.runId}`}>Run {f.runId}</Link>: {f.message}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    }
                />
            )}
            <div style={{ marginBottom: 12 }}>
                <Input.Search
                    placeholder="Filter by flow cell ID..."
                    allowClear
                    value={flowCellFilterInput}
                    onChange={(e) => setFlowCellFilterInput(e.target.value)}
                    onSearch={(val) => { setFlowCellFilter(val); setPage(1) }}
                    style={{ width: 280 }}
                />
            </div>
            <Table
                dataSource={data?.runs || []}
                rowKey="id"
                rowSelection={rowSelection}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.total,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} runs`,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps) },
                }}
                columns={[
                    {
                        title: "Details",
                        dataIndex: "id",
                        align: "center",
                        render: (id: number) => (
                            <Tooltip title="View run">
                                <EyeOutlined style={{ color: "blue" }} onClick={() => navigate(`/admin/run/${id}`)} />
                            </Tooltip>
                        ),
                    },
                    {
                        title: "Run ID",
                        dataIndex: "id",
                        sorter: (a, b) => a.id - b.id,
                        render: (id: number) => <Link to={`/admin/run/${id}`}>{id}</Link>,
                    },
                    {
                        title: "Status",
                        dataIndex: "status",
                        render: (status: string, record: any) => (
                            <Dropdown
                                menu={{
                                    items: RUN_STATUS_OPTIONS.map((opt) => ({
                                        key: opt.value,
                                        label: opt.label,
                                    })),
                                    onClick: ({ key }) => {
                                        if (key !== status) {
                                            handleStatusChange(record.id, key as RunStatusType)
                                        }
                                    },
                                    selectedKeys: [status],
                                }}
                                trigger={["click"]}
                            >
                                <Button
                                    style={getStatusStyle(status)}
                                    loading={updateRunMutation.isPending}
                                >
                                    {RUN_STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status}
                                </Button>
                            </Dropdown>
                        ),
                    },
                    {
                        title: "Nextflow",
                        dataIndex: "id",
                        align: "center",
                        render: (id: number) => {
                            const status = nextflowStatuses[id] || "not running"
                            return (
                                <span
                                    style={{
                                        display: "inline-block",
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                        fontSize: "0.85em",
                                        backgroundColor: status === "running" ? "#52c41a" : "#d9d9d9",
                                        color: status === "running" ? "white" : "#666",
                                        fontWeight: "500"
                                    }}
                                >
                                    {status === "running" ? "running" : "idle"}
                                </span>
                            )
                        },
                    },
                    {
                        title: "Plates",
                        dataIndex: "plateIds",
                        render: (plateIds: number[]) =>
                            plateIds && plateIds.length > 0 ? (
                                plateIds.map((pid, i) => (
                                    <span key={pid}>
                                        <Link to={`/admin/plate/${pid}`}>{pid}</Link>
                                        {i < plateIds.length - 1 && ", "}
                                    </span>
                                ))
                            ) : (
                                <span style={{ color: "#bbb" }}>None</span>
                            ),
                    },
                    {
                        title: "Orders",
                        dataIndex: "orderIds",
                        render: (orderIds: number[]) =>
                            Array.isArray(orderIds) && orderIds.length > 0 ? (
                                orderIds.map((oid, i) => (
                                    <span key={oid}>
                                        <Link to={`/admin/order/${oid}`}>{oid}</Link>
                                        {i < orderIds.length - 1 && ", "}
                                    </span>
                                ))
                            ) : (
                                <span style={{ color: "#bbb" }}>None</span>
                            ),
                    },
                    {
                        title: "Users",
                        dataIndex: "userNames",
                        render: (userNames: string[], record: any) => {
                            // If we have user names from the backend
                            if (Array.isArray(userNames) && userNames.length > 0) {
                                return <span>{userNames.join(", ")}</span>
                            }
                            
                            // Fallback: if we only have orderIds, show count
                            if (Array.isArray(record.orderIds) && record.orderIds.length > 0) {
                                return (
                                    <span style={{ color: "#888" }}>
                                        {record.orderIds.length} order{record.orderIds.length > 1 ? 's' : ''}
                                    </span>
                                )
                            }
                            
                            return <span style={{ color: "#bbb" }}>None</span>
                        },
                    },
                    {
                        title: "Samples",
                        dataIndex: "nSamples",
                        align: "center",
                        render: (nSamples: number) =>
                            nSamples && nSamples > 0 ? (
                                <span style={{ fontWeight: "bold" }}>{nSamples}</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>0</span>
                            ),
                    },
                    {
                        title: "Type",
                        dataIndex: "type",
                    },
                    {
                        title: "Device",
                        dataIndex: "device",
                        render: (device: string) =>
                            device || <span style={{ color: "#bbb" }}>N/A</span>,
                    },
                    {
                        title: "FlowCell",
                        dataIndex: "flowCellId",
                        sorter: (a, b) => {
                            const aValue = a.flowCellId || ""
                            const bValue = b.flowCellId || ""
                            const compareFlowCell = aValue.localeCompare(bValue)
                            if (compareFlowCell === 0) {
                                return b.id - a.id
                            }
                            return compareFlowCell
                        },
                        render: (flowCellId: string) =>
                            flowCellId || <span style={{ color: "#bbb" }}>N/A</span>,
                    },
                    {
                        title: "FlowCell ID",
                        dataIndex: "flowCellDbId",
                        align: "center",
                        sorter: (a, b) => {
                            const aValue = a.flowCellDbId || 0
                            const bValue = b.flowCellDbId || 0
                            return aValue - bValue
                        },
                        render: (flowCellDbId: number) =>
                            flowCellDbId ? (
                                <Link to={`/admin/flowcell/${flowCellDbId}`}>{flowCellDbId}</Link>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Washed",
                        dataIndex: "flowCellWashed",
                        align: "center",
                        render: (washed: boolean, record: any) => {
                            // No flow cell assigned
                            if (washed === undefined || washed === null) {
                                return <span style={{ color: "#bbb" }}>-</span>
                            }
                            
                            // If flow cell is washed, always show green checkmark
                            if (washed) {
                                return <span style={{ color: "#52c41a", fontSize: "18px", fontWeight: "bold" }}>✓</span>
                            }
                            
                            // Flow cell is not washed - only show red X for the most recent run
                            const isMostRecentRun = record.flowCellId && mostRecentRunForFlowCell.get(record.flowCellId) === record.id
                            
                            if (isMostRecentRun) {
                                return <span style={{ color: "#ff4d4f", fontSize: "18px", fontWeight: "bold" }}>✗</span>
                            } else {
                                // For older runs of unwashed flow cell, show as washed
                                return <span style={{ color: "#52c41a", fontSize: "18px", fontWeight: "bold" }}>✓</span>
                            }
                        },
                    },
                     {
                        title: "New Cell",
                        dataIndex: "new_cell",
                        align: "center",
                        render: (new_cell: boolean) =>
                            new_cell ? (
                                <span style={{ color: "#52c41a", fontWeight: "bold" }}>✓</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Pores",
                        dataIndex: "pores",
                        align: "center",
                        render: (pores: number) =>
                            pores !== null && pores !== undefined ? (
                                <span>{pores.toLocaleString()}</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Reads",
                        dataIndex: "nreads",
                        align: "center",
                        render: (reads: number) =>
                            reads ? (
                                <span>{reads.toLocaleString()}</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Duration (hrs)",
                        dataIndex: "duration",
                        align: "center",
                        sorter: (a, b) => {
                            const aValue = a.duration || 0
                            const bValue = b.duration || 0
                            return aValue - bValue
                        },
                        render: (duration: number) =>
                            duration ? (
                                <span>{duration}</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Used",
                        dataIndex: "used",
                        align: "center",
                        render: (used: boolean) =>
                            used ? (
                                <span style={{ color: "#52c41a", fontWeight: "bold" }}>✓</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                    {
                        title: "Died After",
                        dataIndex: "cell_died",
                        align: "center",
                        render: (cell_died: boolean) =>
                            cell_died ? (
                                <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>✓</span>
                            ) : (
                                <span style={{ color: "#bbb" }}>-</span>
                            ),
                    },
                   

                    {
                        title: "Notes",
                        dataIndex: "notes",
                        render: (notes: string) =>
                            notes && notes.length > 25 ? (
                                <span title={notes}>{notes.slice(0, 25)}...</span>
                            ) : (
                                notes || <span style={{ color: "#bbb" }}></span>
                            ),
                    },
                    {
                        title: "Created",
                        dataIndex: "ts",
                        render: (ts: string) => new Date(ts).toLocaleString(),
                    },
                ]}
            />
        </ContainerFull>
    )
}
